// src/merch/useContour.js — live contour cut line for contour-cut zones
// (die-cut stickers). Recomputed in a worker shortly after the design
// changes; returns { [zoneId]: { rings, warnings } } in trim-box mm.

import { useEffect, useRef, useState } from "react";
import { printAreaFor, BLEED_MM } from "./products";

let worker = null, seq = 0;
const waiting = new Map();
function getWorker() {
  if (worker) return worker;
  worker = new Worker(new URL("./contourWorker.js", import.meta.url), { type: "module" });
  worker.onmessage = (e) => { const cb = waiting.get(e.data.id); waiting.delete(e.data.id); cb?.(e.data); };
  return worker;
}

const PX_PER_MM = 4; // preview resolution (the print file uses 6)

function alphaFrom(canvas, sticker) {
  const area = printAreaFor(sticker);
  const S = canvas.width;
  // trim box + bleed, so art running past the edge still counts
  const bx = (BLEED_MM / sticker.widthMm) * (area.x1 - area.x0), by = (BLEED_MM / sticker.heightMm) * (area.y1 - area.y0);
  const sx = (area.x0 - bx) * S, sy = (area.y0 - by) * S, sw = (area.x1 - area.x0 + bx * 2) * S, sh = (area.y1 - area.y0 + by * 2) * S;
  const mmW = sticker.widthMm + BLEED_MM * 2, mmH = sticker.heightMm + BLEED_MM * 2;
  const w = Math.round(mmW * PX_PER_MM), h = Math.round(mmH * PX_PER_MM);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h).data;
  const a = new Uint8Array(w * h);
  for (let i = 0; i < a.length; i++) a[i] = d[i * 4 + 3];
  return { a, w, h, pxPerMm: w / mmW };
}

export function useContour(sides, composites, opts) {
  const [map, setMap] = useState({});
  const timers = useRef({});
  const key = JSON.stringify(opts);
  useEffect(() => {
    for (const z of sides) {
      if (!z.sticker?.contour) continue;
      const canvas = composites[z.id];
      clearTimeout(timers.current[z.id]);
      if (!canvas) continue;
      timers.current[z.id] = setTimeout(() => {
        const st = z.sticker;
        const { a, w, h, pxPerMm } = alphaFrom(canvas, st);
        const id = ++seq;
        waiting.set(id, ({ rings = [], warnings = [] }) => {
          // grid mm (bleed corner = 0) → trim-box mm, kept within the bleed
          const lim = (v, max) => Math.min(max + BLEED_MM, Math.max(-BLEED_MM, v - BLEED_MM));
          const r = rings.map((ring) => ring.map(([x, y]) => [lim(x, st.widthMm), lim(y, st.heightMm)]));
          setMap((m) => ({ ...m, [z.id]: { rings: r, warnings } }));
        });
        getWorker().postMessage({ id, alpha: a, w, h, pxPerMm, opts }, [a.buffer]);
      }, 120);
    }
  }, [sides.map((z) => z.id).join("|"), composites, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);
  return map;
}
